import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link2, Check } from "lucide-react";
import CinematicController from "./CinematicController";
import { buildSnapshotPath } from "@/lib/decisionGraphSnapshot";

// Bottom event-timeline overlay. Ticks are keyboard-focusable buttons so the whole
// sequence can be scrubbed via Tab + Enter (see keyboard-navigation test). In live
// mode ticks appear as REAL events land; transport is replaced by the live marker.
export default function EventTimeline({ events, cursor, playing, live, runId, onScrub, onPlay, onPause, onRestart }) {
  const { t } = useTranslation();
  return (
    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[var(--qn-glass)] to-transparent px-4 pt-8 pb-3"
      data-testid="dg-timeline" data-live={live ? "true" : "false"} role="group" aria-label="Event timeline">
      <div className="flex items-center gap-3">
        {live
          ? <LiveMarker />
          : <CinematicController playing={playing} onPlay={onPlay} onPause={onPause} onRestart={onRestart} />}
        <div className="flex-1 flex items-center gap-1 overflow-x-auto py-1">
          {events.map((ev, i) => (
            <motion.button key={ev.event_id || ev.sequence} data-testid={`dg-timeline-event-${ev.sequence}`}
              initial={live ? { scale: 0, opacity: 0 } : false} animate={{ scale: 1, opacity: 1 }}
              onClick={() => onScrub(i)} title={ev.event_type} aria-label={ev.event_type}
              aria-current={i === cursor ? "true" : undefined} disabled={live}
              className={`shrink-0 h-2 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)] ${
                i === cursor ? "w-7 bg-[var(--qn-primary)]"
                  : i < cursor ? "w-3 bg-[var(--qn-primary-hover)]" : "w-3 bg-[var(--qn-border-hover)]"}`} />
          ))}
          {live && events.length === 0 && (
            <span className="text-[11px] text-[var(--qn-foreground-subtle)]" data-testid="dg-timeline-live-empty">{t("decisionGraph.live.waiting")}</span>
          )}
        </div>
        <div className="text-[11px] font-mono text-[var(--qn-foreground-muted)] shrink-0" data-testid="dg-timeline-label">
          {events[cursor]?.event_type}
        </div>
        <ShareFrame runId={runId} sequence={events[cursor]?.sequence} />
      </div>
    </div>
  );
}

function LiveMarker() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 h-8 px-2.5 rounded-lg bg-[var(--qn-error-muted)] border border-[var(--qn-error)] text-[11px] font-semibold tracking-[0.14em] uppercase text-[var(--qn-foreground)]"
      data-testid="dg-live-marker" role="status">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--qn-error)] opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--qn-error)]" />
      </span>
      {t("decisionGraph.live.marker")}
    </div>
  );
}

function ShareFrame({ runId, sequence }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const path = buildSnapshotPath(runId, sequence);
  if (!path) return null;
  const copy = async () => {
    const url = `${window.location.origin}${path}`;
    try { await navigator.clipboard.writeText(url); } catch { window.prompt(t("decisionGraph.snapshot.copyFallback"), url); }
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  };
  return (
    <button onClick={copy} data-testid="dg-share-frame-btn" title={t("decisionGraph.snapshot.share")} aria-label={t("decisionGraph.snapshot.share")}
      data-snapshot-path={path}
      className="w-8 h-8 rounded-lg flex items-center justify-center bg-[var(--qn-surface-hover)] hover:bg-[var(--qn-card-hover)] text-[var(--qn-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)] shrink-0">
      {copied ? <Check className="w-4 h-4 text-[var(--qn-success)]" data-testid="dg-share-copied" /> : <Link2 className="w-4 h-4" />}
    </button>
  );
}
